import {FC, useState} from 'react'
import {Button} from 'react-bootstrap'
import {DAILY_DEAL_ACTIONS, IDailyDealModel} from '../cors/_models'
import DailyDealTableRow from '../daily-deal/DailyDealTableRow'
import DailyDealSelector from '../daily-deal/DailyDealSelector'
import {useIntl} from 'react-intl'
import {applyDailyDealProduct, dailyDealAction} from "../cors/_requests";
import SharedData from "../../common/SharedData";

interface IProps {
    products: IDailyDealModel[]
    refreshData: () => Promise<void>;
}

const DailyDealTable: FC<IProps> = ({products, refreshData}) => {

    const {formatMessage} = useIntl()
    const {currentUser, language} = SharedData()
    const [isApplying, setIsApplying] = useState(false)
    const [showSelector, setShowSelector] = useState(false)

    const doDailyDealAction = async (action: DAILY_DEAL_ACTIONS, supplierDealId: number): Promise<any> => {
        try {
            await dailyDealAction(
                supplierDealId,
                action,
                currentUser?.token
            )
            refreshData();
        } catch {
            return
        }
    }

    // Step 6: finalize the staged deals — go-live (apply-daily-deals writes the
    // per-offer specials + SUPER_DAILY_DEALS tag + recomputes the buy-box winner).
    const finalizeDailyDeals = async () => {
        try {
            setIsApplying(true)
            await applyDailyDealProduct(currentUser?.token, language)
            window.location.reload()
        } catch {
            setIsApplying(false)
        }
    }

    return (
        <>
            {/* Popup picker — opens on demand so the page stays uncluttered. */}
            <DailyDealSelector
                show={showSelector}
                onClose={() => setShowSelector(false)}
                currentUser={currentUser}
                language={language}
                refreshData={refreshData}
            />

            {/* Action bar: open the picker. */}
            <div className='d-flex flex-wrap align-items-center justify-content-end gap-2 mb-5'>
                <Button variant='primary' onClick={() => setShowSelector(true)}>
                    <i className='fas fa-plus me-1' /> {formatMessage({id: 'select_products_for_deal'})}
                </Button>
            </div>

            {/* Review the staged products, then finalize (go-live). */}
            {products.length > 0 ? (
                <>
                    <div className='card card-bordered'>
                        <div className='card-header border-0 pt-5'>
                            <h3 className='card-title align-items-start flex-column m-0'>
                                <span className='card-label fw-bold fs-4 d-flex align-items-center'>
                                    {formatMessage({id: 'daily_deal_review_title'})}
                                    <span className='badge badge-light-primary ms-2'>{products.length}</span>
                                </span>
                            </h3>
                        </div>
                        <div className='card-body py-3'>
                            <div className='table-responsive'>
                                <table className='table table-row-bordered table-row-gray-200 align-middle gy-3'>
                                    <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='ps-4 rounded-start'>{formatMessage({id: 'image'})}</th>
                                        <th>{formatMessage({id: 'prod_name'})}</th>
                                        <th>{formatMessage({id: 'barcode'})}</th>
                                        <th>{formatMessage({id: 'model_num'})}</th>
                                        <th className='px-3 text-nowrap'>{formatMessage({id: 'qty'})}</th>
                                        <th className='px-3 text-nowrap'>{formatMessage({id: 'sale_price'})}</th>
                                        <th className='px-3 text-nowrap'>{formatMessage({id: 'discount_percentage'})}</th>
                                        <th className='px-3 text-nowrap'>{formatMessage({id: 'discount_date'})}</th>
                                        <th className='pe-4 rounded-end'>{formatMessage({id: 'actions'})}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {products &&
                                        products.map((product) => (
                                            <DailyDealTableRow
                                                onAction={doDailyDealAction}
                                                key={product.supplier_deal_id}
                                                product={product}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Finalize (go-live) — under the selected-products list. */}
                    <div className='d-flex justify-content-end mt-4'>
                        <Button variant='success' disabled={isApplying} onClick={finalizeDailyDeals}>
                            {formatMessage({id: 'add_daily_deal'})}
                        </Button>
                    </div>
                </>
            ) : (
                <div className='text-center text-muted py-10'>
                    <i className='fas fa-bolt fs-2x d-block mb-3 text-gray-400' />
                    {formatMessage({id: 'select_products_for_deal'})}
                </div>
            )}
        </>
    )
}

export default DailyDealTable
